import React, { useEffect, useState } from "react";
import { getLoan, getLoanStatement } from "../../helpers/fakebackend_helper";
import BreadCrumb from "Common/BreadCrumb";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";

interface TableData {
  label: string;
  value?: string;
  link?: boolean;
}

interface LoanPayment {
  shareNumber: number;
  amountDate: string;
  balance: number;
  capital: number;
  interest: number;
  defaultInterest: number;
  totalRemaining: number;
  others: number;
  quota: number;
  capitalBalance: number;
  status: string;
  loanPaymentState: any;
}

const formatCurrency = (value: number | null | undefined): string => {
  const number = typeof value === "number" ? value : 0;
  return `Q ${number.toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDuration = (duration: number | undefined, period: string | undefined): string => {
  if (!duration || !period) return "-";
  const periodMap: Record<string, string> = {
    "MENSUAL": "meses",
    "TRIMESTRAL": "trimestres",
    "SEMESTRAL": "semestres",
    "ANUAL": "años"
  };
  return `${duration} ${periodMap[period] || ""}`;
};

const LoanView = ({ className }: any) => {
  const { loanId } = useParams();
  const [loan, setLoan] = useState<any>({});
  const [loanPayments, setLoanPayments] = useState([]);
  const [loanApplication, setLoanApplication] = useState<any>({});
  const [customer, setCustomer] = useState<any>({});
  const { showLoader, hideLoader } = useLoader();

  const StatusBadge = ({ code }: { code: string }) => {
    const getBadgeProps = (code: string) => {
      switch (code) {
        case "VIG":
          return {
            label: "Vigente",
            bg: "bg-blue-100",
            text: "text-blue-600",
            dark: "dark:bg-blue-500/20 dark:text-blue-400",
          };
        case "PAG":
          return {
            label: "Pagado",
            bg: "bg-green-100",
            text: "text-green-600",
            dark: "dark:bg-green-500/20 dark:text-green-400",
          };
        case "PARCIAL":
          return {
            label: "Pago parcial",
            bg: "bg-yellow-100",
            text: "text-yellow-600",
            dark: "dark:bg-yellow-500/20 dark:text-yellow-400",
          };
        case "ATR":
          return {
            label: "Atrasado",
            bg: "bg-red-100",
            text: "text-red-600",
            dark: "dark:bg-red-500/20 dark:text-red-400",
          };
        default:
          return {
            label: "No Aplica",
            bg: "bg-gray-100",
            text: "text-gray-600",
            dark: "dark:bg-gray-500/20 dark:text-gray-400",
          };
      }
    };

    const { label, bg, text, dark } = getBadgeProps(code);
    return (
      <span
        className={`px-2.5 py-0.5 inline-block text-xs font-medium rounded border border-transparent ${bg} ${text} ${dark}`}
      >
        {label}
      </span>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!loanId) return;

      showLoader();
      try {
        const response = await getLoan(loanId);
        const loanData = response.data || {};
        setLoan(loanData);
        setLoanPayments(loanData?._embedded.loanPayments || []);
        setLoanApplication(loanData?._embedded.loanApplication || []);
        setCustomer(loanData?._embedded.loanApplication.customer || {});
      } catch (error) {
        toast.error("Error fetching loan: " + error);
      } finally {
        hideLoader();
      }
    };
    fetchData();
  }, [loanId]);

  const handleDownloadStatement = async () => {
    if (!loanId) return;

    showLoader();
    try {
      let response = await getLoanStatement(loanId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Hoja de resumen.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error al descargar el estado de cuenta: " + error);
    } finally {
      hideLoader();
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Ver" pageTitle="Prestamos" />

      <div className={className}>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 2xl:grid-cols-12">
          <div className="col-span-12">
            <div className="card">
              <div className="card-body">
                <div className="flex flex-col md:flex-row justify-between mb-6">
                  <h6 className="text-lg font-semibold text-slate-800 dark:text-zink-100">Resumen del Préstamo</h6>
                  <Link
                    to="#!"
                    onClick={handleDownloadStatement}
                    className="flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 border border-red-500 rounded hover:bg-red-500 hover:text-white transition"
                  >
                    <i className="ri-download-2-line mr-2" />
                    Descargar resumen
                  </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-lg p-5 shadow-sm bg-white dark:bg-zink-700">
                    <h6 className="text-base font-semibold mb-4 text-slate-800 dark:text-zink-100">Datos del Préstamo</h6>
                    <div className="space-y-2">
                      {[
                        ["Número de Préstamo", loan?.loanNumber],
                        ["Monto Principal", formatCurrency(loan?.principalAmount)],
                        ["Interes a Pagar", formatCurrency(loan?.interestAmount)],
                        ["Gastos Administrativos", formatCurrency(loan?.administrativeExpenses)],
                        ["Monto a Desembolsar", formatCurrency(loan?.disbursedAmount)],
                        ["Duración", formatDuration(loan?.loanDuration, loanApplication?.durationPeriod)],
                        ["Fecha de Desembolso", loan?.loanReleasedDate],
                        ["Fecha de Vencimiento", loan?.loanDueDate],
                        ["Tasa de Interés", `${loan?.interestRate}%`],
                        ["Periodo de Pago", loan?.durationPeriod],
                        ["Balance", formatCurrency(loan?.balance)],
                      ].map(([label, value], idx) => (
                        <div className="flex justify-between" key={idx}>
                          <span className="text-sm text-slate-500 dark:text-zink-200">{label}:</span>
                          <span className="font-medium text-slate-800 dark:text-zink-100">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg p-5 shadow-sm bg-white dark:bg-zink-700">
                    <h6 className="text-base font-semibold mb-4 text-slate-800 dark:text-zink-100">Datos del Cliente</h6>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500 dark:text-zink-200">Nombre:</span>
                        <span className="font-medium text-slate-800 dark:text-zink-100">{customer.names}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500 dark:text-zink-200">Apellidos:</span>
                        <span className="font-medium text-slate-800 dark:text-zink-100">{customer.surnames}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500 dark:text-zink-200">DPI:</span>
                        <span className="font-medium text-slate-800 dark:text-zink-100">{customer.dpi}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500 dark:text-zink-200">NIT:</span>
                        <span className="font-medium text-slate-800 dark:text-zink-100">{customer.nit}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-6">
              <div className="card-body">
                <h6 className="mb-3 text-15">Detalle de cuotas</h6>
                <div className="overflow-x-auto">
                  <table className="w-full whitespace-nowrap">
                    <thead className="ltr:text-left rtl:text-right">
                      <tr>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Cuota</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Fecha</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Saldo</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Capital</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Interés</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Mora</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Otros</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Pendiente</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Cuota</th>
                        <th className="px-3.5 py-2.5 font-semibold border-b">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanPayments.map((item: LoanPayment, index: number) => (
                        <tr key={index}>
                          <td className="px-3.5 py-2.5 border-y">{item.shareNumber}</td>
                          <td className="px-3.5 py-2.5 border-y">{item.amountDate}</td>
                          <td className="px-3.5 py-2.5 border-y">{formatCurrency(item.capitalBalance)}</td>
                          <td className="px-3.5 py-2.5 border-y">{formatCurrency(item.capital)}</td>
                          <td className="px-3.5 py-2.5 border-y">{formatCurrency(item.interest)}</td>
                          <td className="px-3.5 py-2.5 border-y">{formatCurrency(item.defaultInterest)}</td>
                          <td className="px-3.5 py-2.5 border-y">{formatCurrency(item.others)}</td>
                          <td className="px-3.5 py-2.5 border-y">{formatCurrency(item.totalRemaining)}</td>
                          <td className="px-3.5 py-2.5 border-y">{formatCurrency(item.quota)}</td>
                          <td className="px-3.5 py-2.5 border-y">
                            <StatusBadge code={item.loanPaymentState?.code} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default LoanView;
